import { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { enUS } from "date-fns/locale";
import "chartjs-adapter-date-fns";

function BarChartAdverse({ data, period, unit, displayLegend }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  // Create chart on mount
  useEffect(() => {
    const now = new Date();
    now.setHours(12, 0, 0, 0);

    const chart = new Chart(canvasRef.current, {
      type: "bar",
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2.6,
        layout: { padding: 0 },
        scales: {
          x: {
            stacked: true,
            type: "time",
            title: {
              text: "Date",
              display: true,
              font: { family: "Helvetica", size: 15, weight: "bold" },
            },
            time: {
              unit,
              displayFormats: {
                day: "MMM dd",
                week: "MMM dd",
                month: "MMM dd",
              },
            },
            adapters: {
              date: {
                locale: enUS,
              },
            },
            max: now,
            min: new Date(now.getTime() - period * 24 * 60 * 60 * 1000),
          },
          y: {
            stacked: true,
            title: {
              text: "Amount",
              display: true,
              font: { family: "Helvetica", size: 15, weight: "bold" },
            },
            ticks: {
              stepSize: 1,
            },
          },
        },
        plugins: {
          legend: {
            display: displayLegend,
            position: "bottom",
            onHover: function (e) {
              e.native.target.style.cursor = "pointer";
            },
            onLeave: function (e) {
              e.native.target.style.cursor = "default";
            },
          },
          tooltip: {
            callbacks: {
              title: function (context) {
                let label = context[0].label;
                let title = label.slice(0, -15);
                return title;
              },
            },
          },
        },
      },
      data,
    });

    chartRef.current = chart;

    return () => {
      chart.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update data when it changes
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.data = data;
    chart.update();
  }, [data]);

  // Update axes when period or unit changes
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const now = new Date();
    now.setHours(12, 0, 0, 0);
    const xScale = chart.options.scales.x;
    if (xScale) {
      xScale.max = now;
      xScale.min = new Date(now.getTime() - period * 24 * 60 * 60 * 1000);
      if (xScale.time) xScale.time.unit = unit;
    }
    chart.update();
  }, [period, unit]);

  // Update legend visibility if it changes
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (chart.options.plugins && chart.options.plugins.legend) {
      chart.options.plugins.legend.display = displayLegend;
      chart.update();
    }
  }, [displayLegend]);

  return <canvas ref={canvasRef} />;
}

export default BarChartAdverse;
